import type { Channel, CopyJob, CopyManager, File, Job, Pagination, Simplecut, ValidationError, Location, Compositor, Destination, FileStatus, FileStatusTree, JobStatus, JobStatusTree, TypedTimeline, LocationMergedTimeline } from "./models"
import { api } from "./index"
import { config, contract } from "../stores"
import { get } from "svelte/store"

export function baseUrl(url: string) {
  let _url: URL
  const appConfig = get(config)

  if(appConfig.apiUrl) {
    const apiUrl = appConfig.apiUrl.startsWith("http") ? appConfig.apiUrl : appConfig.apiUrl.startsWith("/") ? window.location.origin + appConfig.apiUrl : window.location.origin + "/" + appConfig.apiUrl
    _url = new URL(url, apiUrl.endsWith("/") ? apiUrl : apiUrl + "/")
  } else {
    _url = new URL(url)
  }

  return _url
}

export function buildUrl(url: string) {
  const appConfig = get(config)
  const token = ` ${appConfig?.user?.token?.type || ""} ${appConfig?.user?.token?.token}`.trim()
  const c = get(contract)

  let _url = baseUrl(url)

  // authorization must be first. Otherwise the access manager doesn't like it
  _url.searchParams.set("authorization", token)
  _url.searchParams.set("gl-contract-id", c === "global" ? "" : c.id || "")

  if (url?.includes("localhost") || appConfig.isStandalone) {
    _url.searchParams.set("gl-user-id", appConfig.user?.data.id || "")
    _url.searchParams.set("gl-policies", appConfig.user?.policies?.map(p => p.id).join(",") || "")
  }


  return _url.toString()
}

export async function getChannels(limit = 10, offset = 0, q = "") {
  return api.get<Pagination<Channel>>("/channels", {params: {limit, offset, q}})
}

export async function getChannel(uuid: string) {
  return api.get<Channel>("/channel/" + uuid)
}

export async function createChannel(channel: Channel) {
  return api.post<Channel>("/channel", channel)
}

export async function patchChannel(uuid: string, channel: Channel) {
  return api.patch<Channel>("/channel/" + uuid, channel)
}

export async function deleteChannel(uuid: string) {
  return api.delete<Channel>("/channel/" + uuid)
}

export async function getChannelTimeline(uuid: string, data: object) {
  return api.post<LocationMergedTimeline[]>("/channels/" + uuid + "/timeline", data)
}

export async function syncChannel(uuid: string) {
  return api.post<Channel>("/channel/" + uuid + "/sync", null)
}

export async function getCopymanagers(limit = 10, offset = 0) {
  return api.get<Pagination<CopyManager>>("/copy-managers", {params: {limit, offset}})
}

export async function getCopymanager(uuid: string) {
  return api.get<CopyManager>("/copy-manager/" + uuid)
}

export async function createCopymanager(copymanager: CopyManager) {
  return api.post<CopyManager>("/copy-manager", copymanager)
}

export async function patchCopymanager(uuid: string, copymanager: CopyManager) {
  return api.patch<CopyManager>("/copy-manager/" + uuid, copymanager)
}

export async function deleteCopymanager(uuid: string) {
  return api.delete("/copy-manager/" + uuid)
}

export async function getCopyjobs(limit = 10, offset = 0, q = "") {
  return api.get<Pagination<CopyJob>>("/copy-jobs", {params: {limit, offset, q}})
}

export async function getCopyjob(uuid: string) {
  return api.get<CopyJob>("/copy-job/" + uuid)
}

export async function getLocations(limit = 10, offset = 0) {
  return api.get<Pagination<Location>>("/locations", {params: {limit, offset}})
}

export async function getLocation(uuid: string) {
  return api.get<Location>("/location/" + uuid)
}

export async function createLocation(location: Location) {
  return api.post<Location>("/location", location)
}

export async function patchLocation(uuid: string, location: Location) {
  return api.patch<Location>("/location/" + uuid, location)
}

export async function deleteLocation(uuid: string) {
  return api.delete("/location/" + uuid)
}

export async function getCompositors(limit = 10, offset = 0) {
  return api.get<Pagination<Compositor>>("/compositors", {params: {limit, offset}})
}

export async function getCompositor(uuid: string) {
  return api.get<Compositor>("/compositor/" + uuid)
}

export async function createCompositor(compositor: Compositor) {
  return api.post<Compositor>("/compositor", compositor)
}

export async function patchCompositor(uuid: string, compositor: Compositor) {
  return api.patch<Compositor>("/compositor/" + uuid, compositor)
}

export async function deleteCompositor(uuid: string) {
  return api.delete("/compositor/" + uuid)
}

export async function getDestinations(limit = 10, offset = 0) {
  return api.get<Pagination<Destination>>("/destinations", {params: {limit, offset}})
}

export async function getDestination(uuid: string) {
  return api.get<Destination>("/destination/" + uuid)
}

export async function createDestinationS3(destination: Destination) {
  return api.post<Destination>("/destination/s3", destination)
}

export async function patchDestinationS3(uuid: string, destination: Destination) {
  return api.patch<Destination>("/destination/s3/" + uuid, destination)
}

export async function deleteDestination(uuid: string) {
  return api.delete("/destination/" + uuid)
}

export async function getJobs(limit = 10, offset = 0) {
  return api.get<Pagination<Job>>("/jobs", {params: {limit, offset}})
}

export async function getJobsStatus(limit = 10, offset = 0, q = "") {
  return api.get<Pagination<JobStatus>>("/jobs/status", {params: {limit, offset, q}})
}

export async function getJobsStatusTree(limit = 10, offset = 0) {
  return api.get<Pagination<JobStatusTree>>("/jobs/status/tree", {params: {limit, offset}})
}

export async function getJob(uuid: string) {
  return api.get<Job>("/job/" + uuid)
}

export async function deleteJob(uuid: string) {
  return api.delete<Job>("/job/" + uuid)
}

export async function rerunJob(uuid: string) {
  return api.post<Job>("/job/" + uuid + "/rerun", null)
}

export async function cancelJob(uuid: string) {
  return api.post<Job>("/job/" + uuid + "/cancel", null)
}

export async function reuploadJob(uuid: string) {
  return api.post<Job>("/job/" + uuid + "/reupload", null)
}

export async function getFiles(limit = 10, offset = 0) {
  return api.get<Pagination<File>>("/files", {params: {limit, offset}})
}

export async function getFilesStatus(limit = 10, offset = 0) {
  return api.get<Pagination<FileStatus>>("/files/status", {params: {limit, offset}})
}

export async function getFilesStatusTree(limit = 10, offset = 0) {
  return api.get<Pagination<FileStatusTree>>("/files/status/tree", {params: {limit, offset}})
}

export async function getFile(uuid: string) {
  return api.get<File>("/file/" + uuid)
}

export async function downloadFile(uuid: string) {
  return api.get("/file/" + uuid + "/download", {responseType: "blob"})
}

export async function simplecut(simplecut: Simplecut) {
  return api.post<Job | ValidationError>("/simplecut", simplecut)
}

export function getOpenApiDefinition() {
  return api.get("/docs/openapi.json")
}